import { useEffect, useMemo, useRef, useState } from 'react';

import { NoData } from '@/components/legacy-ui/NoData';
import { normalizeFilters } from '@/sections/insights/queryBuilder/components/QuerySplitDisplay/QuerySplitVisualizer/utils/normalizeFilter';
import { validateNormalizedFilters } from '@/sections/insights/queryBuilder/components/QuerySplitDisplay/QuerySplitVisualizer/utils/validateNormalizedFilter';
import { useQueryData } from '@/sections/insights/queryBuilder/state/useQueryData';
import type { FlatGraphEdge } from '@/sections/insights/queryBuilder/types/FlatGraph';
import type { NodeDetails, QueryFilter } from '@/types';

import { ConnectorLine } from './ConnectorLine';
import { EdgeCard } from './EdgeCard';
import { NodeCard } from './NodeCard';
import type { LinearPathItem, QuerySimpleVisualizerProps } from './types';
import { linearizePath } from './utils/linearizePath';

/**
 * QuerySimpleVisualizer - Vertical modal chain visualization for linear queries
 *
 * Displays query structures as a vertical chain of expandable cards.
 * Supports linear queries only (no branching).
 */
export const QuerySimpleVisualizer = ({
  onExpandChange,
  editable = false,
}: QuerySimpleVisualizerProps) => {
  const { queryState, dispatch } = useQueryData();

  // Linearize the flatGraph with error boundary for circular paths
  const linearPath = useMemo(() => {
    if (!queryState?.flatGraph) return null;
    try {
      return linearizePath(queryState.flatGraph);
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === 'Circular path detected'
      ) {
        return null; // Treat as error state
      }
      throw error; // Re-throw unexpected errors
    }
  }, [queryState]);

  // Compute initial expanded IDs from linearPath
  const initialExpandedIds = useMemo(() => {
    if (!linearPath) return new Set<string>();
    return new Set(
      linearPath.filter(item => item.type === 'node').map(item => item.node.id)
    );
  }, [linearPath]);

  // Initialize with all node IDs expanded by default
  const [expandedIds, setExpandedIds] =
    useState<Set<string>>(initialExpandedIds);

  // Track previous linearPath to detect changes
  const prevLinearPathRef = useRef<LinearPathItem[] | null>(null);

  // Initialize expandedIds when linearPath first becomes available (during render)
  if (linearPath !== prevLinearPathRef.current) {
    prevLinearPathRef.current = linearPath;
    // Only initialize if expandedIds is currently empty
    if (linearPath && expandedIds.size === 0) {
      // React allows setState during render for initialization based on props
      setExpandedIds(initialExpandedIds);
    }
  }

  // Ref to track pending auto-expansions (doesn't cause re-renders)
  const lastExpandedIdRef = useRef<string | null>(null);

  // Determine which node is deletable (last node, only if query has >1 node)
  const deletableNodeId = useMemo(() => {
    if (!linearPath || linearPath.length <= 1) return null;

    // Find last node in path (iterate backwards, skip edges)
    for (let i = linearPath.length - 1; i >= 0; i--) {
      const item = linearPath[i];
      if (item.type === 'node') {
        return item.node.id;
      }
    }
    return null;
  }, [linearPath]);

  // Handle expand/collapse
  const handleToggle = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      onExpandChange?.(id, next.has(id));
      return next;
    });
  };

  // Handle add relationship after node
  const handleAddRelationship = (afterNodeId: string) => {
    dispatch({
      type: 'ADD_EDGE_AFTER',
      afterNodeId,
    });
  };

  // Handle node deletion (last node only)
  const handleDeleteNode = (nodeId: string) => {
    dispatch({
      type: 'REMOVE_NODE',
      nodeId,
    });
  };

  // Handle update node filters
  const handleUpdateFilters = (nodeId: string, filters: QueryFilter[]) => {
    const node = queryState?.flatGraph?.nodes.get(nodeId);
    if (!node) return;

    dispatch({
      type: 'UPDATE_NODE',
      nodeId,
      data: {
        ...node.data,
        filters,
      },
    });
  };

  // Handle update node label
  const handleUpdateLabel = (nodeId: string, newLabel: string) => {
    const node = queryState?.flatGraph?.nodes.get(nodeId);
    if (!node) return;

    dispatch({
      type: 'UPDATE_NODE',
      nodeId,
      data: {
        ...node.data,
        labels: [newLabel] as NodeDetails['labels'],
        filters: [], // Clear filters when label changes (entities have different fields)
      },
    });
  };

  // Handle update edge metadata (Length, Optional, Not Exists)
  const handleUpdateEdgeData = (
    edgeId: string,
    partialData: Partial<FlatGraphEdge['data']>
  ) => {
    const edge = queryState?.flatGraph?.edges.get(edgeId);
    if (!edge) return;

    dispatch({
      type: 'UPDATE_EDGE',
      edgeId,
      data: {
        ...edge.data,
        ...partialData,
      },
    });
  };

  // Handle update edge filters
  const handleUpdateEdgeFilters = (edgeId: string, filters: QueryFilter[]) => {
    const edge = queryState?.flatGraph?.edges.get(edgeId);
    if (!edge) return;

    dispatch({
      type: 'UPDATE_EDGE',
      edgeId,
      data: {
        ...edge.data,
        filters,
      },
    });
  };

  // Scroll to newly expanded items
  useEffect(() => {
    if (!lastExpandedIdRef.current) return;

    const id = lastExpandedIdRef.current;
    // Check both node and edge cards
    const element = document.querySelector(
      `[data-testid="node-card-${id}"], [data-testid="edge-card-${id}"]`
    );

    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      lastExpandedIdRef.current = null; // Clear after scrolling
    }
  }, [expandedIds]); // Trigger when expandedIds changes (expansion happened)

  // Empty state - no queryState or flatGraph
  if (!queryState?.flatGraph) {
    return (
      <div className="text-chariot-text-secondary p-4">No query to display</div>
    );
  }

  // Branching or circular error
  if (linearPath === null) {
    return (
      <div className="rounded-lg border border-purple-400 bg-purple-50 p-4">
        <div className="font-medium text-purple-600">
          Cannot display this query. Use the graphical UI instead.
        </div>
      </div>
    );
  }

  const validateLinearPath = (linearPath: LinearPathItem[]): boolean => {
    for (const item of linearPath) {
      const filters =
        item.type === 'node' ? item.node.data.filters : item.edge.data.filters;

      const normalized = normalizeFilters(filters);
      const result = validateNormalizedFilters(normalized);
      if (!result.valid) {
        console.error(
          'Failed to normalize query: ' + result.reason,
          normalized
        );
        return false;
      }
    }

    return true;
  };

  if (!validateLinearPath(linearPath)) {
    return (
      <div
        className="mr-8 flex h-full flex-col items-center justify-center"
        data-testid="query-builder-landing-state"
      >
        <NoData
          title="Query Builder Simple Visualizer"
          description="The query you selected is not compatible with the Simple Visualizer. Please open the query in the Graph Visualizer, or try a different query."
          className="flex-grow"
        />
      </div>
    );
  }

  // Render vertical chain
  return (
    <>
      <div className="relative flex h-full flex-col overflow-y-auto">
        {/* Vertical chain */}
        <div className="p-4">
          <div className="mx-auto max-w-2xl space-y-0">
            {linearPath.map((item, index) => {
              if (item.type === 'node') {
                return (
                  <div key={item.node.id}>
                    <NodeCard
                      node={item.node}
                      isExpanded={expandedIds.has(item.node.id)}
                      onToggle={handleToggle}
                      editable={editable}
                      onAddRelationship={handleAddRelationship}
                      onUpdateFilters={handleUpdateFilters}
                      onUpdateLabel={handleUpdateLabel}
                      onDeleteNode={handleDeleteNode}
                      isDeletable={item.node.id === deletableNodeId}
                    />
                    {index < linearPath.length - 1 && <ConnectorLine />}
                  </div>
                );
              } else {
                return (
                  <div key={item.edge.id}>
                    <EdgeCard
                      edge={item.edge}
                      isExpanded={expandedIds.has(item.edge.id)}
                      onToggle={handleToggle}
                      editable={editable}
                      onUpdateEdgeData={handleUpdateEdgeData}
                      onUpdateEdgeFilters={handleUpdateEdgeFilters}
                    />
                    {index < linearPath.length - 1 && <ConnectorLine />}
                  </div>
                );
              }
            })}
          </div>
        </div>
      </div>
    </>
  );
};
